package floris.graphics.backoffice.pages.admin.projects.manage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionProperty
import com.varabyte.kobweb.compose.css.Visibility
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignContent
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.visibility
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaTrashCan
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.ImageChooser
import floris.graphics.backoffice.components.VideoChooser
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.backoffice.components.sections.DetailsSection
import floris.graphics.core.constants.Res
import com.materialdesignsystem.components.widgets.DangerFilledButton
import com.materialdesignsystem.components.widgets.DsBorderRadius
import com.materialdesignsystem.components.widgets.DsCreateCard
import com.materialdesignsystem.components.widgets.DsEditableArea
import com.materialdesignsystem.components.widgets.DsEditableField
import com.materialdesignsystem.components.widgets.DsMultiSelect
import com.materialdesignsystem.components.widgets.DsReadOnlyField
import com.materialdesignsystem.components.widgets.DsSelect
import com.materialdesignsystem.components.widgets.DsVideo
import com.materialdesignsystem.components.widgets.FilledButton
import com.materialdesignsystem.components.widgets.TextButton
import com.materialdesignsystem.extensions.fileChooser
import com.materialdesignsystem.toColorScheme
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignContent
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text

// TODO [TicketNumber]: Give a correct error message when more than 250 characters are entered in the subtitle field

@Page("/admin/projects/manage")
@Composable
fun ManageProjectPage() {

    val context = rememberPageContext()
    val projectId = remember(context.route) {
        context.route.params["id"]
    }

    val viewModel = remember(projectId) { ManageProjectViewModel(projectId) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collect {
            when (it) {
                // TODO [TicketNumber]: Route is still in the history stack, so we need to remove it...
                ManageProjectUiEvent.NavigateBack -> window.history.back()
                ManageProjectUiEvent.ShowError -> window.alert("An error occurred while processing your request.")
            }
        }
    }

    ManageProjectPageContent(state, viewModel::onEvent)
}

private const val VIDEO_ASPECT_RATIO_WIDTH = 16
private const val VIDEO_ASPECT_RATIO_HEIGHT = 9

@Composable
fun ManageProjectPageContent(
    state: ManageProjectState,
    onEvent: (ManageProjectEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()
    val colorScheme = ColorMode.current.toColorScheme

    PageLayout(
        title = if (state.isCreationFlow) "Project aanmaken" else "Project bewerken"
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.LG) 60.percent else 90.percent)
                .fillMaxHeight()
                .maxWidth(800.px)
                .padding(topBottom = 58.px),
            verticalArrangement = Arrangement.spacedBy(24.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            ImageChooser(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(VIDEO_ASPECT_RATIO_WIDTH, VIDEO_ASPECT_RATIO_HEIGHT),
                id = "thumbnail_chooser",
                label = "Project Thumbnail",
                imageUrl = state.thumbnailUrl,
                horizontalAlignment = Alignment.Start,
                centerText = "Click here to upload the project's preview (hero) video thumbnail.",
                isLoading = state.uploadingThumbnail,
                previewRoundedCorners = true,
                onDeleteClicked = { onEvent(ManageProjectEvent.RemoveLogo) },
                onFileSelected = { onEvent(ManageProjectEvent.UpdateThumbnail(it)) }
            )

            VideoChooser(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(VIDEO_ASPECT_RATIO_WIDTH, VIDEO_ASPECT_RATIO_HEIGHT),
                id = "preview_video_chooser",
                label = "Project Preview Video",
                videoUrl = state.previewVideoUrl,
                horizontalAlignment = Alignment.Start,
                centerText = "Click here to upload the project's preview (hero) video.",
                isLoading = state.uploadingVideo,
                onFileSelected = { onEvent(ManageProjectEvent.UpdatePreviewVideo(it)) }
            )

            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = "video-url",
                label = "Video URL",
                value = state.videoUrl,
                type = InputType.Url,
                onValueChange = { onEvent(ManageProjectEvent.UpdateVideoUrl(it)) },
                placeholder = "Enter project video URL (YouTube or Vimeo)",
                valid = state.isVideoUrlValid
            )

            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = "title",
                label = "Title",
                value = state.title,
                onValueChange = { onEvent(ManageProjectEvent.UpdateTitle(it)) },
                placeholder = "Enter project title",
                valid = state.isTitleValid
            )

            // TODO [TicketNumber]: Add a rich text editor for content (Not sure about this yet)
            DsEditableArea(
                modifier = Modifier.fillMaxWidth(),
                id = "subtitle",
                label = "Subtitle",
                value = state.subtitle,
                onValueChange = { onEvent(ManageProjectEvent.UpdateSubtitle(it)) },
                placeholder = "Enter project subtitle"
            )

            // TODO [TicketNumber]: Add a rich text editor for content
            DsEditableArea(
                modifier = Modifier.fillMaxWidth(),
                id = "description",
                label = "Description",
                value = state.description,
                onValueChange = { onEvent(ManageProjectEvent.UpdateDescription(it)) },
                placeholder = "Enter project description"
            )

            DsMultiSelect(
                modifier = Modifier.fillMaxWidth(),
                id = "select-categories",
                label = "Categories",
                placeholder = "Select Categories",
                items = state.categories.map { it.name },
                selectedItems = state.selectedCategories.map { it.name },
                onItemSelect = { selected ->
                    val selectedCategory = state.categories.find { it.name == selected }
                    onEvent(ManageProjectEvent.UpdateMultiCategory(selectedCategory))
                }
            )

            DsMultiSelect(
                modifier = Modifier.fillMaxWidth(),
                id = "select-techstacks",
                label = "Techstacks",
                placeholder = "Select Techstacks",
                items = state.techstacks.map { it.name },
                selectedItems = state.selectedTechstacks.map { it.name },
                onItemSelect = { selected ->
                    val selectedTechstack = state.techstacks.find { it.name == selected }
                    onEvent(ManageProjectEvent.UpdateMultiTechstack(selectedTechstack))
                }
            )

            SimpleGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(
                        rowGap = 24.px,
                        columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
                    ),
                numColumns = numColumns(1, 2, 2)
            ) {
                DsSelect(
                    modifier = Modifier.fillMaxWidth(),
                    id = "select-client",
                    label = "Client",
                    placeholder = "Select Client",
                    items = state.contributors.map { it.name },
                    preselectedItem = state.originalProject.client?.name,
                    onItemSelect = { index, _ ->
                        val selectedClient = state.contributors.getOrNull(index)
                        onEvent(ManageProjectEvent.UpdateClient(selectedClient))
                    }
                )

                DsSelect(
                    modifier = Modifier.fillMaxWidth(),
                    id = "select-agency",
                    label = "Agency",
                    placeholder = "Select Agency",
                    items = state.contributors.map { it.name },
                    preselectedItem = state.originalProject.agency?.name,
                    onItemSelect = { index, _ ->
                        val selectedAgency = state.contributors.getOrNull(index)
                        onEvent(ManageProjectEvent.UpdateAgency(selectedAgency))
                    }
                )

                DsSelect(
                    modifier = Modifier.fillMaxWidth(),
                    id = "select-designer",
                    label = "Designer",
                    placeholder = "Select Designer",
                    items = state.contributors.map { it.name },
                    preselectedItem = state.originalProject.design?.name,
                    onItemSelect = { index, _ ->
                        val selectedDesigner = state.contributors.getOrNull(index)

                        onEvent(ManageProjectEvent.UpdateDesigner(selectedDesigner))
                    }
                )

                DsSelect(
                    modifier = Modifier.fillMaxWidth(),
                    id = "select-animator",
                    label = "Animator",
                    placeholder = "Select Animator",
                    items = state.contributors.map { it.name },
                    preselectedItem = state.originalProject.animator?.name,
                    onItemSelect = { index, _ ->
                        val selectedAnimator = state.contributors.getOrNull(index)

                        onEvent(ManageProjectEvent.UpdateAnimator(selectedAnimator))
                    }
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth()
                    .margin(topBottom = 32.px)
            ) {
                Label(
                    attrs = Modifier
                        .margin(bottom = 0.5.cssRem)
                        .toAttrs(),
                    forId = "project-media-grid"
                ) {
                    Text(value = "Project Media")
                }

                SimpleGrid(
                    modifier = Modifier
                        .id("project-media-grid")
                        .fillMaxWidth()
                        .gap(
                            rowGap = 24.px,
                            columnGap = 24.px
                        ),
                    numColumns = numColumns(base = 1, sm = 2),
                ) {
                    val modifier = Modifier
                        .fillMaxSize()
                        .aspectRatio(16, 9)
                        .borderRadius(10.px)

                    state.frameMediaFiles.forEach { mediaFile ->
                        Box(
                            modifier = MediaFrameStyle.toModifier()
                        ) {
                            FaTrashCan(
                                modifier = Modifier
                                    .id("RemoveOption")
                                    .zIndex(1)
                                    .size(40.px)
                                    .borderRadius(100.percent)
                                    .cursor(Cursor.Pointer)
                                    .background(colorScheme.error)
                                    .margin(top = 8.px, right = 8.px)
                                    .color(colorScheme.onError)
                                    .onClick {
                                        onEvent(ManageProjectEvent.RemoveMediaFile(mediaFile))
                                    }
                                    .textAlign(TextAlign.Center)
                                    .align(Alignment.TopEnd)
                                    .alignContent(AlignContent.Center),
                                size = IconSize.LG
                            )

                            if (mediaFile.mimeType.startsWith("video/")) {
                                DsVideo(
                                    videoUrl = mediaFile.url.toString(),
                                    modifier = modifier
                                        .objectFit(ObjectFit.Cover)
                                )
                            } else if (mediaFile.mimeType.startsWith("image/")) {
                                Image(
                                    mediaFile.url.toString(),
                                    modifier = modifier
                                        .overflow(Overflow.Clip)
                                        .objectFit(ObjectFit.Cover)
                                )
                            }
                        }
                    }

                    DsCreateCard(
                        modifier = modifier,
                        text = "Add extra media for this project",
                        iconSize = 48.px,
                        onClick = {
                            document.fileChooser(
                                multiple = true,
                                accept = "image/*,video/*"
                            ) { file ->
                                onEvent(ManageProjectEvent.UpdateFrameFiles(file))
                            }
                        }
                    )
                }
            }

            if (state.isEditFlow) {
                DetailsSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = Res.String.Metadata
                ) {
                    Column(Modifier.fillMaxWidth(), Arrangement.spacedBy(16.px)) {
                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.ID,
                            value = state.originalProject.id
                        )

                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.CreatedAt,
                            value = state.originalProject.createdAt
                        )

                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.UpdatedAt,
                            value = state.originalProject.updatedAt
                        )

                        DangerFilledButton(
                            onClick = { onEvent(ManageProjectEvent.Delete) }
                        ) {
                            Text(Res.String.Delete)
                        }
                    }
                }
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(16.px)
            ) {
                FilledButton(
                    enabled = state.hasChanged,
                    onClick = { onEvent(ManageProjectEvent.Save) }
                ) {
                    Text(Res.String.Save)
                }

                TextButton(
                    borderRadius = DsBorderRadius(8.px),
                    onClick = { onEvent(ManageProjectEvent.Cancel) }
                ) {
                    Text(Res.String.Cancel)
                }
            }
        }
    }
}

val MediaFrameStyle = CssStyle {
    cssRule(" > #RemoveOption") {
        Modifier
            .transition(Transition.of(property = TransitionProperty.All, duration = 0.ms, delay = 150.ms))
            .visibility(Visibility.Hidden)
    }

    cssRule(":hover > #RemoveOption") {
        Modifier
            .visibility(Visibility.Visible)
    }
}
